/**
 * HTTP client.
 *
 * Every request sends the session cookie (credentials: 'include'), which is why the API
 * has to allow this exact origin in CORS: a wildcard is rejected by the browser when
 * credentials travel with the request.
 */
import { apiUrl } from '@/shared/config';

import { ApiError, UNEXPECTED_MESSAGE } from './errors';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

/** Offset pagination envelope, mirrors cubas.domain.schemas.common.Page. */
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = new URL(`${apiUrl}${path}`);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * `fetch`, with «no answer at all» turned into an `ApiError` the screen can explain.
 *
 * The browser rejects with a `TypeError` whose message depends on the browser
 * («Failed to fetch», «Load failed», «NetworkError when attempting...»), and that text
 * used to reach the screen as it was. An abort is left alone: nothing here aborts, and
 * the one that happens (the tab navigating away) must not become a message.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === 'AbortError') throw caught;
    throw ApiError.network();
  }
}

/** A 2xx whose body is not the JSON the screen expects: a proxy page, a cut connection. */
async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(response.status, UNEXPECTED_MESSAGE);
  }
}

async function request<T>(path: string, init: RequestInit, params?: QueryParams): Promise<T> {
  // A FormData body must NOT get a Content-Type from here: the browser writes
  // `multipart/form-data` with the boundary it chose, and a hand written header without
  // that boundary is a body the server cannot split.
  const json = Boolean(init.body) && !(init.body instanceof FormData);
  const response = await send(buildUrl(path, params), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
    ...init,
  });

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return readJson<T>(response);
}

/** A file the API returns in the body, with the name it gave it (if the header is readable). */
export interface ApiFile {
  blob: Blob;
  filename: string | null;
}

/**
 * `filename="BORRADOR-000123.pdf"` → `BORRADOR-000123.pdf`. Null when there is no header,
 * which is what a cross origin response looks like unless the API exposes it (in
 * production the front and the API share a host, so it is there).
 */
export function filenameFromDisposition(header: string | null): string | null {
  const match = header?.match(/filename="([^"]+)"/);
  return match?.[1] ?? null;
}

async function requestFile(path: string): Promise<ApiFile> {
  const response = await send(buildUrl(path), { method: 'GET', credentials: 'include' });
  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }
  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('Content-Disposition')),
  };
}

export const api = {
  get: <T>(path: string, params?: QueryParams): Promise<T> =>
    request<T>(path, { method: 'GET' }, params),

  /** A GET whose answer is a file, not JSON: the draft's PDF (contract v10.18). */
  getFile: (path: string): Promise<ApiFile> => requestFile(path),

  /** `params` go in the query string, like a GET's: «Marcar todo como leído» takes the
   * list's filters there (contract v10.22). */
  post: <T>(path: string, body?: unknown, params?: QueryParams): Promise<T> =>
    request<T>(
      path,
      {
        method: 'POST',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      },
      params,
    ),

  /** A multipart POST: files. The browser sets the content type and the boundary. */
  postForm: <T>(path: string, form: FormData): Promise<T> =>
    request<T>(path, { method: 'POST', body: form }),

  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),

  /** A DELETE, with a JSON body when the endpoint wants a reason (the archive's trash). */
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
};
